import { makeAutoObservable, runInAction } from 'mobx';
import { HelperCore, HelpOther } from '@src/common/helper';
import { request } from '@src/common';
import Loader from '@src/common/helper/loader';
import chatStoreManager from './chat/manager';
import globalStore from './global';
import Local from '@/common/helper/local';
import localRobot, { RobotsMap } from '@/common/localRobot.js';
import { sendBroadcast } from '@/hooks/useBroadcast';
import deleteMenuStore from '@/components/navbar-v2/delete-mune/store';
import urlStore from './chat/urlStore';
import slsLogger from '@public/log/sls';

const isCollapse_cache = Local.getItem('isCollapse');
const showHistory_cache = Local.getItem('showHistory') || true; // 20250208 改为默认展开
class Store {
  historyList = [];
  specifyList = [];
  showNavbarRoutes = ['/', '/sosearch', '/examples', '/chathome','/chat', '/aichat', '/agentchat', '/tools/aiagent/chat', '/teamhome']; //首页、智能体广场、会话页才展示Navbar
  chatModels = {};
  specifyCache = {};
  getting = false;
  isCollapse = isCollapse_cache;
  // 屏幕宽度小于1280时，默认收起(收起的体验不太好，先去掉)
  // document.body.clientWidth > 1280 ? isCollapse_cache || false : true;
  showHistory = showHistory_cache === null ? true : showHistory_cache;
  historyNpt = '';
  timerConversationTitles = null;
  timeConversationTitles = 3000;

  // 1: AI搜索, 2: 大模型
  sourceId = 0;
  curConversationId = null; // 当前会话ID

  constructor() {
    makeAutoObservable(this);

    if (!HelperCore.IS_PROD) {
      window._navbarStore = this;
    }
  }

  setSourceId(id) {
    if(id !== 1 && id !== 2 && id !== 3) {
      return;
    }

    if (this.sourceId !== id) {
      this.reset();
    }
    runInAction(() => {
      this.sourceId = id;
    });
  }

  setCurConversationId = (id) => {
    runInAction(() => {
      this.curConversationId = id;
    });
  }

  getPageSize() {
    const history_wrap = document.getElementById('history_wrap');
    // 卫士侧边栏，没有左侧栏
    if (!history_wrap) {
      return;
    }
    const maxCount = history_wrap.offsetHeight / 67;
    if (maxCount < 30) {
      return 30;
    } else {
      return 50;
    }
  }

  setShowHistory = flag => {
    this.showHistory = flag;
    Local.setItem('showHistory', flag);
  };

  setIsCollapse = flag => {
    this.isCollapse = flag;
    Local.setItem('isCollapse', flag);
  };

  /** 仅用于移除显示中的还没创建好的对话 */
  deleteHistoryItem = (conversation_id) => {
    runInAction(() => {
      this.historyList = this.historyList.filter(
        item => item.conversation_id != conversation_id
      );
      console.info('>>> 已移除会话', conversation_id);
    });
  }

  requestClearSo=(params)=>{
    if(!params.cid && !params.is_all){
      return Promise.resolve({code: 0})
    }
    slsLogger.log({
      key : "chat",
      action : "removeConversation",
      ext : JSON.stringify(params),
    });
    return request.post('/batch/remove/conversation?appsource=so', params);
  }

  requestClearBot=(params)=>{
    if(!params.cid && !params.is_all){
      return Promise.resolve({code: 0})
    }
    slsLogger.log({
      key : "chat",
      action : "removeConversation",
      ext : JSON.stringify(params),
    });
    return request.post('/batch/remove/conversation', params);
  }

  clearHistory = (conversation_id, needGotoHome, is_all, ai_agent=5) => {
    //都处理成数组，按批量删除处理
    const isBatch = typeof conversation_id !== 'string';
    conversation_id = isBatch ? conversation_id : [conversation_id];
    const list = this.historyList.filter(item=>conversation_id.includes(item.conversation_id));
    const cidList_so = list.filter(item=>item.data_source == 1);
    const cidList_bot = list.filter(item=>item.data_source != 1);
    Promise.all([
      this.requestClearSo({cid: cidList_so.map(item=>item.conversation_id).join(','), is_all, ai_agent}),
      this.requestClearBot({cid: cidList_bot.map(item=>item.conversation_id).join(','), is_all, ai_agent})
    ]).then(resList => {
      //console.log(resList);
      if(resList[0]?.code == 0 && resList[1]?.code == 0){
          HelpOther.toast('删除成功');
          deleteMenuStore.exitEidt();
          runInAction(() => {
            if (is_all) {
              this.historyList.forEach(item => {
                const res = chatStoreManager.delete(item.conversation_id); //historyList可能不全
                HelperCore.log('delete res:' + res);
              });
              this.historyList = [];
            } else {
              conversation_id.forEach(cid => {
                const res = chatStoreManager.delete(cid);
                HelperCore.log('delete res:' + res);
              });
              this.historyList = this.historyList.filter(
                item => !conversation_id.includes(item.conversation_id)
              );
            }
          });
          //删除之后请求下一页数据
          if (this.historyNpt) {
            this.getHistory();
          }
          sendBroadcast('history-changed', {
            removeId: conversation_id.join(','),
            isBatch,
            is_all,
          });

          if (needGotoHome) {
            globalStore.gotoHome(true);
          }
        }
        return resList;
      })
      .catch(Loader.error);
  };

  /**
   * 获取会话历史列表
   */
  getHistory = (first, ai_agent="5") => {
    if (!this.sourceId) {
      return;
    }

    this.controller?.abort();
    this.controller = new AbortController();
    if (first) {
      this.historyNpt = '';
    }

    const url = this.sourceId === 1
      ? '/assistant/combine/conversation/history'
      : '/assistant/conversation/history';
    request
      .get(url, {
        signal: this.controller.signal,
        params: {
          page_size: first ? this.getPageSize() : 10,
          npt: this.historyNpt,
          pin_top: 1,
          title_status: 1,
          ai_agent
        },
      })
      .then(res => {
        const list = res.data?.list || [];
        const npt = res.data?.npt || '';

        // 按页面分类过滤
        const filteredList = list.filter(item => {
          return true;
          if (this.sourceId === 1) {
            // AI搜索
            return item.data_source === this.sourceId;
          }
          else if (this.sourceId === 2) {
            // agent搜索
            return item.data_source === this.sourceId || !item.data_source;
          }
          return false;
        });

        //拼接数字人icon信息
        if (filteredList.length) {
          const role_ids = filteredList.map(item => item.role_id);
          this.getSpecifyList(role_ids);
        }
        runInAction(() => {
          if (first) {
            this.historyList = filteredList;
          } else {
            this.historyList = [...this.historyList, ...filteredList];
          }
          if (filteredList.length) {
            this.historyNpt = npt;
          } else {
            this.historyNpt = ''; // list数据为空时就认为没有下一页了
          }

          this.rollConversationTitles();
        });
      })
      .catch(Loader.error)
      .finally(() => {
        runInAction(() => {
          this.getting = false;
        });
      });
  };
  insertHistory = (conversationId, body) => {
    const {
      agent_id,
      role_id,
      from_aiso,
    } = body;

    // debug
    HelperCore.log("insertHistory", conversationId, body);

    if(!conversationId){
      console.error(`insertHistory conversationId error:`, conversationId);
      return;
    }

    const agentInfo = agent_id ? urlStore.agentList[agent_id] : null;
    const roleInfo = role_id ? this.chatModels[role_id] : null;
    if(agent_id && !agentInfo){
      console.error(`insertHistory agent_id error:`, agent_id);
      return;
    }
    if(role_id && !roleInfo){
      console.error(`insertHistory role_id error:`, role_id);
      return;
    }

    const conversation = {
      ...body,
      ...{
        conversation_id: conversationId,
        title : body.prompt,
        update_time : +new Date,
      },
    };
    if(agentInfo){
      conversation.agent_info = agentInfo;
    }
    // AI 搜索，智能体
    if(from_aiso == 1){
      conversation.data_source = 2;
    }
    this.historyList.unshift(conversation);
  }

  /**
   * 根据会话id查询会话详情,更新最新的会话标题
   * @param {*} param.conversationIds 超级搜索、大模型、智能体（助手）
   * @param {*} param.aiso_conversation_id AI搜索
   * @returns
   */
  getConversationHistoryById = (param) => {
    const { conversationIds, aiso_conversation_id } = param;
    clearTimeout(this.timerConversationTitles);

    if(!conversationIds?.length && !aiso_conversation_id?.length) {
      return;
    }
    const params = {
      conversation_ids: conversationIds.join(","),
    };
    if(aiso_conversation_id){
      params.aiso_conversation_id = aiso_conversation_id.join(",");
    }
    request.post("/assistant/conversation/title/status", params)
    .then(res => {
      const { data } = res;

      const { list = [], aiso_list = [] } = data;
      let hasUpdated = false;
      let needRoll = false;
      [...list, ...aiso_list].forEach(item => {
        if(item.title_status == 2){
          hasUpdated = true;
          const historyItem = this.historyList.find(history => history.conversation_id == item.conversation_id);
          if(historyItem){
            Object.assign(historyItem, {
              title : item.summary_title || item.title,
            });
          }
        }
        else {
          needRoll = true;
        }
      });
      if(hasUpdated){
        this.historyList = HelperCore.copyObj(this.historyList);
      }
      if(needRoll){
        this.timerConversationTitles = setTimeout(() => {
          this.rollConversationTitles();
        }, this.timeConversationTitles);
      }
    });

  }
  // 0 旧数据 1 新会话 2 标题总结完成
  rollConversationTitles () {
    // title_status: 1 生成中， 2 生成完成
    const needUpdateConversations = this.historyList.filter(item => [0, 1].includes(item.title_status));
    // data_source: 1 AIso 普通搜索 2 助手 超级搜索
    const aiSearchConversationIds = needUpdateConversations.filter(item => item.data_source == 1).map(item => item.conversation_id);
    const conversationIds = needUpdateConversations.filter(item => item.data_source != 1).map(item => item.conversation_id);

    if(needUpdateConversations.length){
      this.getConversationHistoryById({ conversationIds, aiso_conversation_id: aiSearchConversationIds });
    }
  }
  setChatModels = list => {
    if (!list.length) return;
    const chatModels = {};
    // 数组转对象
    list.forEach(item => (chatModels[item.id] = item));
    runInAction(() => {
      // 追加
      this.chatModels = {
        ...this.chatModels,
        ...chatModels,
      };
    });
  };
  getSpecifyList = role_ids => {
    role_ids = role_ids.filter(
      id =>
        // cut 消息，role_id 为空
        id &&
        !this.specifyList.find(item => item.id == id) &&
        !this.specifyCache[id] &&
        !RobotsMap[id]
    );
    if (!role_ids.length) return;
    //防止重复请求
    role_ids.forEach(id => {
      if (!this.specifyCache[id]) {
        this.specifyCache[id] = true;
      }
    });
    return request
      .get('/assistant/specify/robot', {
        params: {
          role_ids: HelperCore.removeRepeatArray(role_ids).join(','),
        },
      })
      .then(res => {
        const list = res.data?.list || [];
        const specifyList = [...this.specifyList, ...list];
        this.setChatModels(specifyList);
        runInAction(() => {
          this.specifyList = specifyList;
        });
      });
  };
  getSpecifyModel = role_id => {
    const localModel = localRobot.localModels.filter(
      item => item.id === role_id
    )[0];
    if (localModel) {
      return Promise.resolve(localModel);
    }
    if (this.chatModels[role_id]) {
      return Promise.resolve(this.chatModels[role_id]);
    }
    return request
      .get('/assistant/specify/robot', {
        params: {
          role_ids: role_id,
        },
      })
      .then(res => {
        const list = res.data?.list || [];
        const specifyList = [...this.specifyList, ...list];
        this.setChatModels(specifyList);
        runInAction(() => {
          this.specifyList = specifyList;
        });
        return list.find(item => item.id == role_id);
      });
  };

  /**
   * 是否 deepseek 模型
   * @param {String} role_id
   */
  isDSModel = role_id => {
    const model = this.chatModels[role_id];
    if (!model) {
      return false;
    }
    return (
      model.tag == 'deepseek' ||
      model.localModel ||
      model.model_type === 2 ||
      model.model_type === 3 ||
      /deepseek/i.test(model.name || model.title)
    );
  };

  updateAliasTitle = (conversation_id, alias_title) => {
    runInAction(() => {
      this.historyList.map(item => {
        if (item.conversation_id == conversation_id) {
          item.alias_title = alias_title;
        }
      });
      this.historyList = HelperCore.copyObj(this.historyList);
    });
  };

  updateTopUp = (conversation_id, pin_top_time) => {
    runInAction(() => {
      this.historyList.map(item => {
        if (item.conversation_id == conversation_id) {
          item.pin_top_time = pin_top_time;
        }
      });
      this.historyList = HelperCore.copyObj(this.historyList);
    });
  };

  reset() {
    this.historyList = [];
    this.specifyList = [];
    this.chatModels = {};
    this.specifyCache = {};
    this.historyNpt = '';
  }
}

const navbarStore = new Store();
export default navbarStore;
